import { useState, useMemo, useCallback, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDetailedProjectReport, useDownloadDetailedReportXlsx } from '../../hooks/useReports';
import DetailedReportFilters from '../../components/domain/DetailedReportFilters';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { formatCurrency, formatDisplayDate } from '../../utils/format';
import { MoneyMetricLabel } from '../../components/common/MoneyMetricLabel';
import { computeMarginPercent, formatMarginPercent } from '../../utils/moneyGlossary';
import type { DetailedReportFilters as Filters, EntryRow } from '../../services/api/reports';

type TabId = 'all' | 'roles' | 'projects' | 'tasks';

function filtersToParams(filters: Filters): URLSearchParams {
  const params = new URLSearchParams();
  params.set('projectIds', filters.projectIds.join(','));
  params.set('from', filters.from);
  params.set('to', filters.to);
  if (filters.includeSubprojects) params.set('includeSubprojects', '1');
  if (filters.includeExpenses) params.set('includeExpenses', '1');
  if (filters.includeExtraGains) params.set('includeExtraGains', '1');
  if (filters.includeCosts) params.set('includeCosts', '1');
  if (filters.includeNotApproved) params.set('includeNotApproved', '1');
  if (filters.includeRejected) params.set('includeRejected', '1');
  return params;
}

function paramsToFilters(params: URLSearchParams): Filters | null {
  const projectIdsStr = params.get('projectIds');
  const from = params.get('from');
  const to = params.get('to');
  if (!projectIdsStr || !from || !to) return null;
  const projectIds = projectIdsStr.split(',').map((id) => parseInt(id.trim(), 10)).filter((id) => !isNaN(id));
  if (projectIds.length === 0) return null;
  return {
    projectIds,
    from,
    to,
    includeSubprojects: params.get('includeSubprojects') === '1',
    includeExpenses: params.get('includeExpenses') === '1',
    includeExtraGains: params.get('includeExtraGains') === '1',
    includeCosts: params.get('includeCosts') === '1',
    includeNotApproved: params.get('includeNotApproved') === '1',
    includeRejected: params.get('includeRejected') === '1',
  };
}

function getISOWeek(dateStr: string): { week: number; year: number } {
  const date = new Date(dateStr + 'T00:00:00');
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { week: weekNum, year: d.getUTCFullYear() };
}

function getWeekKey(dateStr: string): string {
  const { week, year } = getISOWeek(dateStr);
  return `${year}-W${week}`;
}

function computeWeekSpans(entries: { date: string; hours?: number; totalBilling?: number }[]) {
  const spans = new Map<number, { label: string; rowSpan: number }>();
  const firstRows = new Set<number>();
  const lastRows = new Set<number>();
  const dayBoundaries = new Set<number>();
  const weekTotals = new Map<number, { hours: number; billing: number }>();
  let i = 0;
  while (i < entries.length) {
    const key = getWeekKey(entries[i]!.date);
    const { week } = getISOWeek(entries[i]!.date);
    let j = i + 1;
    while (j < entries.length && getWeekKey(entries[j]!.date) === key) {
      if (entries[j]!.date !== entries[j - 1]!.date) {
        dayBoundaries.add(j);
      }
      j++;
    }
    let totalHours = 0;
    let totalBilling = 0;
    for (let k = i; k < j; k++) {
      totalHours += entries[k]!.hours ?? 0;
      totalBilling += entries[k]!.totalBilling ?? 0;
    }
    spans.set(i, { label: `Week ${week}`, rowSpan: j - i + 1 });
    weekTotals.set(j - 1, { hours: totalHours, billing: totalBilling });
    firstRows.add(i);
    lastRows.add(j - 1);
    i = j;
  }
  return { spans, firstRows, lastRows, dayBoundaries, weekTotals };
}

export default function ProjectDetailedReportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filtersFromUrl = useMemo(() => paramsToFilters(searchParams), [searchParams]);
  const [filters, setFiltersState] = useState<Filters | null>(filtersFromUrl);
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');

  const setFilters = useCallback((f: Filters) => {
    setFiltersState(f);
    setSearchParams(filtersToParams(f), { replace: true });
  }, [setSearchParams]);

  const { data: report, isLoading, error } = useDetailedProjectReport(filters);
  const downloadXlsx = useDownloadDetailedReportXlsx();
  // The API omits every cost field for anyone who is not admin or finance.
  const hasCosts = report?.totals.costs !== undefined;
  const marginPct = computeMarginPercent(report?.totals.billing ?? 0, report?.totals.costs);

  const handleDownloadXlsx = () => {
    if (filters) {
      const selectedIds = new Set(filters.projectIds);
      const projectNames = report?.projects.filter((p) => selectedIds.has(p.id)).map((p) => p.name);
      downloadXlsx.mutate({ filters, projectNames });
    }
  };

  const handleCreateInvoice = () => {
    if (filters && report) {
      const params = new URLSearchParams();
      // Pass only the originally selected project IDs (not expanded subprojects)
      params.set('projectIds', filters.projectIds.join(','));
      params.set('from', filters.from);
      params.set('to', filters.to);
      navigate(`/invoices/new?${params.toString()}`);
    }
  };

  // Unique users for filter
  const uniqueUsers = useMemo(() => {
    if (!report) return [];
    const names = [...new Set(report.entries.map((e) => e.userName))];
    return names.sort((a, b) => a.localeCompare(b));
  }, [report]);

  // Unique projects for filter
  const uniqueProjects = useMemo(() => {
    if (!report) return [];
    const seen = new Map<number, string>();
    for (const e of report.entries) {
      if (!seen.has(e.projectId)) seen.set(e.projectId, e.projectName);
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [report]);

  // Unique roles (scoped to selected project when one is chosen, otherwise
  // across all entries in the report). Petra: role filter is project-scoped
  // and matches the "By Role" tab grouping key.
  const uniqueRoles = useMemo(() => {
    if (!report) return [];
    const base = selectedProjectId
      ? report.entries.filter((e) => e.projectId === Number(selectedProjectId))
      : report.entries;
    const names = new Set<string>();
    for (const e of base) {
      if (e.roleName) names.add(e.roleName);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [report, selectedProjectId]);

  // Filtered entries based on selected user, project and role
  const filteredEntries = useMemo(() => {
    if (!report) return [];
    let entries = report.entries;
    if (selectedUser) entries = entries.filter((e) => e.userName === selectedUser);
    if (selectedProjectId) entries = entries.filter((e) => e.projectId === Number(selectedProjectId));
    if (selectedRole) entries = entries.filter((e) => e.roleName === selectedRole);
    return entries;
  }, [report, selectedUser, selectedProjectId, selectedRole]);

  // Aggregate tasks from filtered entries
  const filteredTasks = useMemo(() => {
    const taskMap = new Map<string, { task: string; hours: number; entries: number }>();
    for (const entry of filteredEntries) {
      const key = entry.task || '(no task)';
      const existing = taskMap.get(key);
      if (existing) {
        existing.hours += entry.hours;
        existing.entries += 1;
      } else {
        taskMap.set(key, { task: key, hours: entry.hours, entries: 1 });
      }
    }
    return [...taskMap.values()].sort((a, b) => b.hours - a.hours);
  }, [filteredEntries]);

  const filteredTotalHours = useMemo(
    () => filteredEntries.reduce((sum, e) => sum + e.hours, 0),
    [filteredEntries]
  );

  const filteredTotalBilling = useMemo(
    () => filteredEntries.reduce((sum, e) => sum + e.totalBilling, 0),
    [filteredEntries]
  );

  // Compute week spans for row grouping
  const weekSpans = useMemo(() => computeWeekSpans(filteredEntries), [filteredEntries]);

  // Aggregate entries by date + project + role for "By Role" tab
  const roleAggregatedEntries = useMemo(() => {
    const map = new Map<string, { date: string; projectId: number; projectName: string; roleName: string | null; hours: number; totalBilling: number }>();
    for (const e of filteredEntries) {
      const key = `${e.date}|${e.projectId}|${e.roleName || ''}`;
      const existing = map.get(key);
      if (existing) {
        existing.hours += e.hours;
        existing.totalBilling += e.totalBilling;
      } else {
        map.set(key, {
          date: e.date,
          projectId: e.projectId,
          projectName: e.projectName,
          roleName: e.roleName,
          hours: e.hours,
          totalBilling: e.totalBilling,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      if (a.projectName !== b.projectName) return a.projectName.localeCompare(b.projectName);
      return (a.roleName || '').localeCompare(b.roleName || '');
    });
  }, [filteredEntries]);

  const roleWeekSpans = useMemo(() => computeWeekSpans(roleAggregatedEntries), [roleAggregatedEntries]);

  // Group entries by project
  const entriesByProject = filteredEntries.reduce<Record<number, EntryRow[]>>((acc, entry) => {
    const projectEntries = acc[entry.projectId] || [];
    projectEntries.push(entry);
    acc[entry.projectId] = projectEntries;
    return acc;
  }, {});

  const tabs = [
    { id: 'all' as TabId, label: 'Whole Report' },
    { id: 'projects' as TabId, label: 'By Project' },
    { id: 'tasks' as TabId, label: 'By Tasks' },
    { id: 'roles' as TabId, label: 'By Role' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/reports')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Reports
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Detailed Project Report</h1>
      </div>

      <DetailedReportFilters onApply={setFilters} isLoading={isLoading} initialFilters={filtersFromUrl} />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{(error as Error).message}</p>
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {report && (
        <div className="space-y-6">
          {/* Report Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-medium text-gray-900">
                  {report.projects.map((p) => p.name).join(', ')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Period: {formatDisplayDate(report.period.from)} to {formatDisplayDate(report.period.to)}
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={handleDownloadXlsx}
                isLoading={downloadXlsx.isPending}
              >
                Download XLS
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Total Hours</p>
              <p className="text-2xl font-semibold text-gray-900">
                {report.totals.hours.toFixed(1)}
              </p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm text-gray-500">
                <MoneyMetricLabel metric="earned" />
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatCurrency(report.totals.billing)}
              </p>
            </div>
            {/* MKS-332 — the cost and margin tiles are absent, not zeroed,
                for anyone the API withheld them from. */}
            {hasCosts && (
              <>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">
                    <MoneyMetricLabel metric="cost" />
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(report.totals.costs ?? 0)}
                  </p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">
                    <MoneyMetricLabel metric="margin" />
                  </p>
                  <p
                    className={`text-2xl font-semibold ${
                      (marginPct ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {/* MKS-332: the one formula, (Earned − Cost) ÷ Earned. */}
                    {formatMarginPercent(marginPct)}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Billing by Rate Section */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-medium text-gray-900">Billing by Rate</h3>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Project
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Rate
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Hours
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {report.billing.map((row, idx) => (
                  <tr key={`${row.projectId}-${row.billingRate}-${idx}`}>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.projectName}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {formatCurrency(row.billingRate)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {row.hours.toFixed(1)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {formatCurrency(row.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900" colSpan={2}>
                    Total
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                    {report.totals.hours.toFixed(1)}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                    {formatCurrency(report.totals.billing)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Costs Summary (if enabled) */}
          {report.costs && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-medium text-gray-900">Costs Summary</h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Labor Costs</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(report.costs.laborCosts)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Expenses</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(report.costs.expenses)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Costs</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(report.costs.totalCosts)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Extra Gains (if any) */}
          {report.extraGains.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-medium text-gray-900">
                  Extra Gains ({formatCurrency(report.totals.extraGains)})
                </h3>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Project
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Invoice #
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {report.extraGains.map((gain) => (
                    <tr key={gain.id}>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDisplayDate(gain.date)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{gain.projectName}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{gain.description || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {gain.invoiceNumber || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatCurrency(gain.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Expenses (if any) */}
          {report.expenses.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-medium text-gray-900">
                  {/* MKS-332: the expense ROWS stay for everyone; only the
                      cost total above them is admin/finance-only. */}
                  Expenses
                  {report.totals.expenses !== undefined &&
                    ` (${formatCurrency(report.totals.expenses)})`}
                </h3>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Project
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Description
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {report.expenses.map((expense) => (
                    <tr key={expense.id}>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDisplayDate(expense.date)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{expense.projectName}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {expense.description || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatCurrency(expense.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Invoices */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-medium text-gray-900">
                Invoices{' '}
                {report.invoices.length > 0 && (
                  <>
                    — <MoneyMetricLabel metric="invoiced" />:{' '}
                    {formatCurrency(report.totals.invoiced)}
                  </>
                )}
              </h3>
            </div>
            {report.invoices.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 text-center">
                No invoices for this period
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Invoice #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Period
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Projects
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {report.invoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {invoice.invoiceNumber || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {invoice.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDisplayDate(invoice.from)} - {formatDisplayDate(invoice.to)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {invoice.projectNames.join(', ') || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatCurrency(invoice.invoicedAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {/* Uninvoiced section */}
            <div className={`border-t border-gray-200 px-4 py-3 flex justify-between items-center ${report.totals.uninvoicedHours > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
              <div>
                <p className={`text-sm font-medium ${report.totals.uninvoicedHours > 0 ? 'text-amber-800' : 'text-gray-600'}`}>
                  To be invoiced: {report.totals.uninvoicedHours.toFixed(1)} hours ({formatCurrency(report.totals.uninvoicedBilling)})
                </p>
              </div>
              {report.totals.uninvoicedHours > 0 && (
                <Button size="sm" onClick={handleCreateInvoice}>Generate Invoice</Button>
              )}
            </div>
          </div>

          {/* Hours Detail - Tabbed View */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <h3 className="font-medium text-gray-900">Hours Detail</h3>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">All users</option>
                  {uniqueUsers.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                <select
                  value={selectedProjectId}
                  onChange={(e) => {
                    setSelectedProjectId(e.target.value);
                    // Reset role filter when project changes — role options
                    // are scoped to the selected project.
                    setSelectedRole('');
                  }}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">All projects</option>
                  {uniqueProjects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  disabled={uniqueRoles.length === 0}
                >
                  <option value="">All roles</option>
                  {uniqueRoles.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownloadXlsx}
                isLoading={downloadXlsx.isPending}
              >
                Download XLS
              </Button>
            </div>
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="max-h-[calc(100vh-200px)] overflow-auto">
              {activeTab === 'all' && (
                <table className="min-w-full divide-y divide-gray-200" style={{ borderCollapse: 'collapse' }}>
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase bg-gray-50" style={{ width: '40px' }}>
                        Week
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Project
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Task
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Hours
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Billing
                      </th>
                    </tr>
                    <tr className="bg-primary-50 border-b-2 border-b-primary-300">
                      <td colSpan={7} className="px-4 py-2 text-xs font-bold text-primary-700 uppercase text-right">
                        Period Total
                      </td>
                      <td className="px-4 py-2 text-sm font-bold text-primary-900 text-right">
                        {filteredTotalHours.toFixed(1)}
                      </td>
                      <td className="px-4 py-2 text-sm font-bold text-primary-900 text-right">
                        {formatCurrency(filteredTotalBilling)}
                      </td>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredEntries.map((entry, index) => {
                      const weekInfo = weekSpans.spans.get(index);
                      const isFirstRow = weekSpans.firstRows.has(index);
                      const isLastRow = weekSpans.lastRows.has(index);
                      const isDayBoundary = weekSpans.dayBoundaries.has(index);
                      const weekTotal = weekSpans.weekTotals.get(index);
                      const cellBorder = [
                        isFirstRow ? 'border-t-2 border-t-gray-400' : isDayBoundary ? 'border-t border-t-gray-300' : '',
                      ].join(' ');
                      return (
                        <Fragment key={entry.id}>
                          <tr>
                            {weekInfo && (
                              <td
                                rowSpan={weekInfo.rowSpan}
                                className="px-1 py-1 text-xs font-semibold text-gray-500 text-center border-2 border-gray-400 bg-gray-50"
                                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' }}
                              >
                                {weekInfo.label}
                              </td>
                            )}
                            <td className={`px-4 py-2 text-sm text-gray-900 ${cellBorder}`}>{formatDisplayDate(entry.date)}</td>
                            <td className={`px-4 py-2 text-sm text-gray-900 ${cellBorder}`}>{entry.projectName}</td>
                            <td className={`px-4 py-2 text-sm text-gray-900 ${cellBorder}`}>{entry.userName}</td>
                            <td className={`px-4 py-2 text-sm text-gray-500 ${cellBorder}`}>{entry.roleName || '-'}</td>
                            <td className={`px-4 py-2 text-sm text-gray-500 ${cellBorder}`}>{entry.task || '-'}</td>
                            <td className={`px-4 py-2 text-sm text-gray-500 max-w-xs truncate ${cellBorder}`}>
                              {entry.description || '-'}
                            </td>
                            <td className={`px-4 py-2 text-sm text-gray-900 text-right ${cellBorder}`}>
                              {entry.hours.toFixed(1)}
                            </td>
                            <td className={`px-4 py-2 text-sm text-gray-900 text-right ${cellBorder}`}>
                              {formatCurrency(entry.totalBilling)}
                            </td>
                          </tr>
                          {isLastRow && weekTotal && (
                            <tr className="bg-gray-50 border-b-2 border-b-gray-400">
                              <td colSpan={6} className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right uppercase">
                                Week Total
                              </td>
                              <td className="px-4 py-1.5 text-sm font-semibold text-gray-900 text-right">
                                {weekTotal.hours.toFixed(1)}
                              </td>
                              <td className="px-4 py-1.5 text-sm font-semibold text-gray-900 text-right">
                                {formatCurrency(weekTotal.billing)}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {activeTab === 'roles' && (
                <table className="min-w-full divide-y divide-gray-200" style={{ borderCollapse: 'collapse' }}>
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase bg-gray-50" style={{ width: '40px' }}>
                        Week
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Project
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Role
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Hours
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Billing
                      </th>
                    </tr>
                    <tr className="bg-primary-50 border-b-2 border-b-primary-300">
                      <td colSpan={4} className="px-4 py-2 text-xs font-bold text-primary-700 uppercase text-right">
                        Period Total
                      </td>
                      <td className="px-4 py-2 text-sm font-bold text-primary-900 text-right">
                        {filteredTotalHours.toFixed(1)}
                      </td>
                      <td className="px-4 py-2 text-sm font-bold text-primary-900 text-right">
                        {formatCurrency(filteredTotalBilling)}
                      </td>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {roleAggregatedEntries.map((row, index) => {
                      const weekInfo = roleWeekSpans.spans.get(index);
                      const isFirstRow = roleWeekSpans.firstRows.has(index);
                      const isLastRow = roleWeekSpans.lastRows.has(index);
                      const isDayBoundary = roleWeekSpans.dayBoundaries.has(index);
                      const weekTotal = roleWeekSpans.weekTotals.get(index);
                      const cellBorder = [
                        isFirstRow ? 'border-t-2 border-t-gray-400' : isDayBoundary ? 'border-t border-t-gray-300' : '',
                      ].join(' ');
                      return (
                        <Fragment key={`${row.date}-${row.projectId}-${row.roleName || ''}`}>
                          <tr>
                            {weekInfo && (
                              <td
                                rowSpan={weekInfo.rowSpan}
                                className="px-1 py-1 text-xs font-semibold text-gray-500 text-center border-2 border-gray-400 bg-gray-50"
                                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' }}
                              >
                                {weekInfo.label}
                              </td>
                            )}
                            <td className={`px-4 py-2 text-sm text-gray-900 ${cellBorder}`}>{formatDisplayDate(row.date)}</td>
                            <td className={`px-4 py-2 text-sm text-gray-900 ${cellBorder}`}>{row.projectName}</td>
                            <td className={`px-4 py-2 text-sm text-gray-500 ${cellBorder}`}>{row.roleName || '-'}</td>
                            <td className={`px-4 py-2 text-sm text-gray-900 text-right ${cellBorder}`}>
                              {row.hours.toFixed(1)}
                            </td>
                            <td className={`px-4 py-2 text-sm text-gray-900 text-right ${cellBorder}`}>
                              {formatCurrency(row.totalBilling)}
                            </td>
                          </tr>
                          {isLastRow && weekTotal && (
                            <tr className="bg-gray-50 border-b-2 border-b-gray-400">
                              <td colSpan={3} className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right uppercase">
                                Week Total
                              </td>
                              <td className="px-4 py-1.5 text-sm font-semibold text-gray-900 text-right">
                                {weekTotal.hours.toFixed(1)}
                              </td>
                              <td className="px-4 py-1.5 text-sm font-semibold text-gray-900 text-right">
                                {formatCurrency(weekTotal.billing)}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {activeTab === 'projects' && (
                <div className="divide-y divide-gray-200">
                  <div className="px-4 py-2 bg-primary-50 border-b-2 border-b-primary-300 flex items-center justify-between sticky top-0 z-10">
                    <span className="text-xs font-bold text-primary-700 uppercase tracking-wide">
                      Period Total
                    </span>
                    <span className="text-sm font-bold text-primary-900 tabular-nums">
                      {filteredTotalHours.toFixed(1)} h &middot; {formatCurrency(filteredTotalBilling)}
                    </span>
                  </div>
                  {Object.entries(entriesByProject).map(([projectId, entries]) => {
                    const projectName = entries[0]?.projectName || `Project ${projectId}`;
                    const totalHours = entries.reduce((sum, e) => sum + e.hours, 0);
                    const totalBilling = entries.reduce((sum, e) => sum + e.totalBilling, 0);
                    const projWeekSpans = computeWeekSpans(entries);
                    return (
                      <div key={projectId} className="p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium text-gray-900">{projectName}</h4>
                          <span className="text-sm text-gray-500">
                            {totalHours.toFixed(1)} hours | {formatCurrency(totalBilling)}
                          </span>
                        </div>
                        <table className="min-w-full divide-y divide-gray-200" style={{ borderCollapse: 'collapse' }}>
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase" style={{ width: '40px' }}>
                                Week
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Date
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                User
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Role
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Task
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Description
                              </th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                Hours
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {entries.map((entry, index) => {
                              const weekInfo = projWeekSpans.spans.get(index);
                              const isFirstRow = projWeekSpans.firstRows.has(index);
                              const isLastRow = projWeekSpans.lastRows.has(index);
                              const isDayBoundary = projWeekSpans.dayBoundaries.has(index);
                              const weekTotal = projWeekSpans.weekTotals.get(index);
                              const cellBorder = [
                                isFirstRow ? 'border-t-2 border-t-gray-400' : isDayBoundary ? 'border-t border-t-gray-300' : '',
                              ].join(' ');
                              return (
                                <Fragment key={entry.id}>
                                  <tr>
                                    {weekInfo && (
                                      <td
                                        rowSpan={weekInfo.rowSpan}
                                        className="px-1 py-1 text-xs font-semibold text-gray-500 text-center border-2 border-gray-400 bg-gray-50"
                                        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' }}
                                      >
                                        {weekInfo.label}
                                      </td>
                                    )}
                                    <td className={`px-4 py-2 text-sm text-gray-900 ${cellBorder}`}>{formatDisplayDate(entry.date)}</td>
                                    <td className={`px-4 py-2 text-sm text-gray-900 ${cellBorder}`}>{entry.userName}</td>
                                    <td className={`px-4 py-2 text-sm text-gray-500 ${cellBorder}`}>{entry.roleName || '-'}</td>
                                    <td className={`px-4 py-2 text-sm text-gray-500 ${cellBorder}`}>
                                      {entry.task || '-'}
                                    </td>
                                    <td className={`px-4 py-2 text-sm text-gray-500 max-w-xs truncate ${cellBorder}`}>
                                      {entry.description || '-'}
                                    </td>
                                    <td className={`px-4 py-2 text-sm text-gray-900 text-right ${cellBorder}`}>
                                      {entry.hours.toFixed(1)}
                                    </td>
                                  </tr>
                                  {isLastRow && weekTotal && (
                                    <tr className="bg-gray-50 border-b-2 border-b-gray-400">
                                      <td colSpan={5} className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right uppercase">
                                        Week Total
                                      </td>
                                      <td className="px-4 py-1.5 text-sm font-semibold text-gray-900 text-right">
                                        {weekTotal.hours.toFixed(1)}
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              )}

              {activeTab === 'tasks' && (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Task
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Hours
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Entries
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        % of Total
                      </th>
                    </tr>
                    <tr className="bg-primary-50 border-b-2 border-b-primary-300">
                      <td className="px-4 py-2 text-xs font-bold text-primary-700 uppercase text-right">
                        Period Total
                      </td>
                      <td className="px-4 py-2 text-sm font-bold text-primary-900 text-right">
                        {filteredTotalHours.toFixed(1)}
                      </td>
                      <td className="px-4 py-2 text-sm font-bold text-primary-900 text-right">
                        {filteredTasks.reduce((sum, t) => sum + t.entries, 0)}
                      </td>
                      <td className="px-4 py-2 text-sm font-bold text-primary-900 text-right">
                        100%
                      </td>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredTasks.map((task) => (
                      <tr key={task.task}>
                        <td className="px-4 py-3 text-sm text-gray-900">{task.task}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {task.hours.toFixed(1)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {task.entries}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {filteredTotalHours > 0
                            ? ((task.hours / filteredTotalHours) * 100).toFixed(1)
                            : 0}
                          %
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">Total</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                        {filteredTotalHours.toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                        {filteredTasks.reduce((sum, t) => sum + t.entries, 0)}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                        100%
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
